import React, { useEffect, useMemo, useState } from "react";
import InnerPageStructure from "../../../shared/components/generic/inner-page-structure/inner-page-structure";
import CardGeneric from "../../../shared/components/generic/card/card-generic";
import DashboardLineChart from "../components/dashboard-line-chart";
import DashboardHeader from "../pages/header/dashboard-header";
import DateUtils from "../../../shared/utils/DateUtils";
import {
    getCycleBaselineClassName,
} from "../utils/dashboard-status-utils";
import AdditionalInformation from "../../../shared/components/generic/additional-information/additional-information";
import { useDashboardClientContracts } from "../hooks/useDashboardClientContracts";
import PageLoading from "../../../shared/components/generic/page-loading/page-loading";
import { showCommonAlert } from "../../../shared/components/generic/alert/utils/show-common-alert-utils";
import { TokenUtils } from "../../../shared/utils/TokenUtils";
import { useDashboardContractCycles } from "../hooks/useDashboardContractCycles";
import DashboardCyclePanel from "../pages/cycle-panel/dashboard-cycle-panel";
import { useDashboardContractCycle } from "../hooks/useDashboardContractCycle";
import DashboardMonthlySalesSummary from "./sales-summary/dashboard-monthly-sales-summary";
import DashboardVariluxSummary from "./sales-summary/dashboard-varilux-summary";
import DashboardDailySalesSummary from "./sales-summary/dashboard-daily-sales-summary";
import { useDashboardDailyGrowthSimulation } from "../hooks/useDashboardDailyGrowthSimulation";
import MonetaryUtils from "../../../shared/utils/MonetaryUtils";
import {
    buildCycleOptions,
    buildDailyAverageChartData,
    buildDailyAverageTargetOptions,
    buildEquipmentOptions,
    buildMonthlySalesChartData,
    buildPurchaseSummary,
    buildVariluxMonthlyChartData,
    findDailyGrowthRangeByDiscount
} from "../utils/dashboard-data-utils";
import { useDashboardParticipantClients } from "../hooks/useDashboardParticipantClients";
import DashboardClientSearch from "./client-search/dashboard-client-search";
import AppButton from "../../../shared/components/generic/button/app-button";
import "./dashboard.css";

const Dashboard = ({ initialClientContracts = null }) => {
    const [clientSearchDocument, setClientSearchDocument] = useState("");
    const [clientSearchFormResetKey, setClientSearchFormResetKey] = useState(0);
    const [selectedClientDocument, setSelectedClientDocument] = useState("");
    const [selectedParticipantClient, setSelectedParticipantClient] = useState(null);
    const [isClientSearchExpanded, setIsClientSearchExpanded] = useState(true);
    const [simulateGrossValue, setSimulateGrossValue] = useState(false);
    const [dailyAverageTarget, setDailyAverageTarget] = useState("");
    const [backendSimulatedToday] = useState(null);
    const [selectedContractId, setSelectedContractId] = useState("");
    const [selectedEquipment, setSelectedEquipment] = useState("");
    const [selectedCycleId, setSelectedCycleId] = useState("");
    const [hasManualCycleSelection, setHasManualCycleSelection] = useState(false);

    const {
        participantClients,
        participantClientsPagination,
        participantClientsLoading,
        fetchParticipantClients,
    } = useDashboardParticipantClients();

    const {
        clientContracts,
        clientContractsLoading,
        fetchClientContracts,
        clearClientContracts
    } = useDashboardClientContracts(initialClientContracts);

    const {
        contractCycles,
        contractCyclesLoading,
        fetchContractCycles,
        clearContractCycles
    } = useDashboardContractCycles();

    const {
        dashboardCycle,
        dashboardCycleLoading,
        fetchDashboardCycle,
        clearDashboardCycle
    } = useDashboardContractCycle();

    const {
        dailyGrowthSimulation,
        dailyGrowthSimulationLoading,
        fetchDailyGrowthSimulation,
        clearDailyGrowthSimulation
    } = useDashboardDailyGrowthSimulation();

    const isDashboardLoading =
        clientContractsLoading ||
        contractCyclesLoading ||
        dashboardCycleLoading ||
        dailyGrowthSimulationLoading ||
        participantClientsLoading;

    const isEssilorUser = useMemo(() => {
        return TokenUtils.isEssilor();
    }, []);

    const tokenClientDocument = useMemo(() => {
        return TokenUtils.getUserCnpj() || "";
    }, []);

    const clientDocument = useMemo(() => {
        if (isEssilorUser) {
            return selectedClientDocument;
        }

        return tokenClientDocument;
    }, [
        isEssilorUser,
        selectedClientDocument,
        tokenClientDocument
    ]);

    const resetDashboardSelection = () => {
        setSelectedContractId("");
        setSelectedEquipment("");
        setSelectedCycleId("");
        setHasManualCycleSelection(false);
        clearContractCycles();
        clearDashboardCycle();
        clearDailyGrowthSimulation();
    };

    const loadParticipantClients = async ({
        page = 0,
        size = participantClientsPagination.size,
        document = clientSearchDocument
    } = {}) => {
        const result = await fetchParticipantClients({
            page,
            size,
            document
        });

        if (!result.ok) {
            await showCommonAlert({
                title: "Erro",
                message: result.error,
                fallbackMessage: "Não foi possível buscar os clientes.",
                icon: "error",
                customClass: {
                    container: "common-alert__container",
                    confirmButton: "alert-form-button"
                }
            });

            return result;
        }

        return result;
    };

    const handleParticipantClientsPageChange = async ({ selected }) => {
        await loadParticipantClients({
            page: selected,
            size: participantClientsPagination.size,
            document: clientSearchDocument
        });
    };

    const handleSelectParticipantClient = client => {
        if (!client?.document) {
            return;
        }

        resetDashboardSelection();
        clearClientContracts();

        setSelectedParticipantClient(client);
        setSelectedClientDocument(client.document);
        setClientSearchDocument(client.document);
        setIsClientSearchExpanded(false);
    };

    const handleClientSearch = async values => {
        resetDashboardSelection();
        setSelectedClientDocument("");
        setSelectedParticipantClient(null);
        setIsClientSearchExpanded(true);

        const searchDocument =
            values?.clientSearchDocument ?? clientSearchDocument ?? "";

        setClientSearchDocument(searchDocument);

        const result = await loadParticipantClients({
            page: 0,
            size: participantClientsPagination.size,
            document: searchDocument
        });

        if (!result?.ok) {
            return;
        }

        if (result.empty) {
            await showCommonAlert({
                title: "Aviso",
                message: "Nenhum cliente participante encontrado.",
                icon: "warning",
                customClass: {
                    container: "common-alert__container",
                    confirmButton: "alert-form-button"
                }
            });

            setClientSearchDocument("");
            setClientSearchFormResetKey(previous => previous + 1);

            await loadParticipantClients({
                page: 0,
                size: participantClientsPagination.size,
                document: ""
            });
        }
    };

    const handleShowClientSearchAgain = async () => {
        setSelectedParticipantClient(null);
        setSelectedClientDocument("");
        setClientSearchDocument("");
        setIsClientSearchExpanded(true);

        resetDashboardSelection();

        await loadParticipantClients({
            page: 0,
            size: participantClientsPagination.size,
            document: ""
        });
    };

    useEffect(() => {
        if (!isEssilorUser) {
            return;
        }

        const loadInitialParticipantClients = async () => {
            const result = await loadParticipantClients({
                page: 0,
                size: participantClientsPagination.size,
                document: ""
            });

            if (result?.empty) {
                await showCommonAlert({
                    title: "Aviso",
                    message: "Nenhum cliente participante encontrado.",
                    icon: "warning",
                    customClass: {
                        container: "common-alert__container",
                        confirmButton: "alert-form-button"
                    }
                });
            }
        };

        loadInitialParticipantClients();
    }, [isEssilorUser]);

    const loadClientContracts = async () => {
        if (!clientDocument) {
            return;
        }

        const result = await fetchClientContracts(clientDocument);

        if (!result.ok) {
            await showCommonAlert({
                title: "Erro",
                message: result.error,
                fallbackMessage: "Não foi possível carregar os contratos do cliente.",
                icon: "error",
                customClass: {
                    container: "common-alert__container",
                    confirmButton: "alert-form-button"
                }
            });

            return result;
        }

        return result;
    };

    const loadContractCycles = async contractId => {
        if (!clientDocument || !selectedEquipment || !contractId) {
            clearContractCycles();
            return;
        }

        const result = await fetchContractCycles({
            document: clientDocument,
            contractId
        });

        if (!result.ok) {
            await showCommonAlert({
                title: "Erro",
                message: result.error,
                fallbackMessage: "Não foi possível carregar os ciclos do contrato.",
                icon: "error",
                customClass: {
                    container: "common-alert__container",
                    confirmButton: "alert-form-button"
                }
            });

            return result;
        }

        return result;
    };

    const loadDashboardCycle = async ({
        contractId = selectedContractId,
        cycle = selectedCycleId
    } = {}) => {
        if (!clientDocument || !contractId || !cycle) {
            clearDashboardCycle();
            return;
        }

        const result = await fetchDashboardCycle({
            document: clientDocument,
            contractId,
            cycle,
            calculationMode: simulateGrossValue ? "GROSS" : "NET"
        });

        if (!result.ok) {
            await showCommonAlert({
                title: "Erro",
                message: result.error,
                fallbackMessage: "Não foi possível carregar o dashboard do ciclo.",
                icon: "error",
                customClass: {
                    container: "common-alert__container",
                    confirmButton: "alert-form-button"
                }
            });

            return result;
        }

        return result;
    };

    const loadDailyGrowthSimulation = async ({
        contractId = selectedContractId,
        cycle = selectedCycleId
    } = {}) => {
        if (!clientDocument || !contractId || !cycle) {
            clearDailyGrowthSimulation();
            return;
        }

        const result = await fetchDailyGrowthSimulation({
            document: clientDocument,
            contractId,
            cycle,
            calculationMode: simulateGrossValue ? "GROSS" : "NET"
        });

        if (!result.ok) {
            await showCommonAlert({
                title: "Erro",
                message: result.error,
                fallbackMessage: "Não foi possível carregar a simulação de crescimento diário.",
                icon: "error",
                customClass: {
                    container: "common-alert__container",
                    confirmButton: "alert-form-button"
                }
            });

            return result;
        }

        return result;
    };

    useEffect(() => {
        setSelectedContractId("");
        setSelectedEquipment("");
        setSelectedCycleId("");
        setHasManualCycleSelection(false);
        clearContractCycles();
        clearDashboardCycle();
        clearDailyGrowthSimulation();

        if (!clientDocument) {
            return;
        }

        // Evita consultar novamente os contratos recebidos por prop.
        if (!isEssilorUser && initialClientContracts !== null) {
            return;
        }

        loadClientContracts();
    }, [clientDocument, isEssilorUser, initialClientContracts]);

    const equipmentOptions = useMemo(() => {
        return buildEquipmentOptions(clientContracts);
    }, [clientContracts]);

    useEffect(() => {
        if (equipmentOptions.length === 0) {
            setSelectedEquipment("");
            setSelectedContractId("");
            setSelectedCycleId("");
            setHasManualCycleSelection(false);
            clearContractCycles();
            clearDashboardCycle();
            clearDailyGrowthSimulation();
            return;
        }

        const hasSelectedEquipmentInOptions = equipmentOptions.some(option => {
            return String(option.value) === String(selectedEquipment);
        });

        if (hasSelectedEquipmentInOptions) {
            return;
        }

        const firstEquipment = equipmentOptions[0];

        setSelectedEquipment(firstEquipment.value);
        setSelectedContractId(firstEquipment.contractId);
        setSelectedCycleId("");
        setHasManualCycleSelection(false);
    }, [
        equipmentOptions,
        selectedEquipment
    ]);

    const selectedContract = useMemo(() => {
        return clientContracts.find(contract => {
            return String(contract.contractId) === String(selectedContractId);
        });
    }, [clientContracts, selectedContractId]);

    useEffect(() => {
        if (!clientDocument || !selectedEquipment || !selectedContractId) {
            clearContractCycles();
            clearDashboardCycle();
            clearDailyGrowthSimulation();
            return;
        }

        const selectedEquipmentOption = equipmentOptions.find(option => {
            return String(option.value) === String(selectedEquipment);
        });

        const hasValidSelectedContract =
            selectedEquipmentOption &&
            String(selectedEquipmentOption.contractId) === String(selectedContractId);

        if (!hasValidSelectedContract) {
            clearContractCycles();
            clearDashboardCycle();
            clearDailyGrowthSimulation();
            return;
        }

        loadContractCycles(selectedContractId);
    }, [
        clientDocument,
        selectedEquipment,
        selectedContractId,
        equipmentOptions
    ]);

    const cycleOptions = useMemo(() => {
        return buildCycleOptions(contractCycles);
    }, [contractCycles]);

    const selectedCycleOption = useMemo(() => {
        return contractCycles.find(cycle => {
            return String(cycle.value) === String(selectedCycleId);
        });
    }, [contractCycles, selectedCycleId]);

    useEffect(() => {
        if (contractCycles.length === 0 || hasManualCycleSelection) {
            return;
        }

        const contractOpenCycle = selectedContract?.openCycleValue;

        const openCycleFromContract = contractCycles.find(cycle => {
            return String(cycle.value) === String(contractOpenCycle);
        });

        const openCycle =
            openCycleFromContract ||
            contractCycles.find(cycle => cycle.isOpen);

        if (openCycle && String(selectedCycleId) !== String(openCycle.value)) {
            setSelectedCycleId(openCycle.value);
        }
    }, [
        contractCycles,
        selectedContract,
        selectedCycleId,
        hasManualCycleSelection
    ]);

    useEffect(() => {
        if (!selectedContractId || !selectedCycleId) {
            clearDashboardCycle();
            clearDailyGrowthSimulation();
            return;
        }

        loadDashboardCycle({
            contractId: selectedContractId,
            cycle: selectedCycleId
        });

        if (selectedCycleOption?.isClosed) {
            clearDailyGrowthSimulation();
            return;
        }

        loadDailyGrowthSimulation({
            contractId: selectedContractId,
            cycle: selectedCycleId
        });
    }, [
        clientDocument,
        selectedContractId,
        selectedCycleId,
        selectedCycleOption?.isClosed,
        simulateGrossValue
    ]);

    const selectedCycle = dashboardCycle?.cycle;

    const simulatedToday = useMemo(() => {
        return (
            DateUtils.parseDateOnlyToTimestamp(dashboardCycle?.sales?.daily?.currentDay) ||
            DateUtils.parseDateOnlyToTimestamp(backendSimulatedToday) ||
            DateUtils.getDateDaysFromToday(-1).getTime()
        );
    }, [dashboardCycle, backendSimulatedToday]);

    const simulatedTodayLabel = useMemo(() => {
        return DateUtils.formatDateToDayAndMonth(simulatedToday);
    }, [simulatedToday]);

    const isSelectedCycleClosed = useMemo(() => {
        if (dashboardCycle) {
            return Boolean(dashboardCycle.isClosed);
        }

        if (selectedCycleOption) {
            return Boolean(selectedCycleOption.isClosed);
        }

        return false;
    }, [dashboardCycle, selectedCycleOption]);

    const purchaseSummary = useMemo(() => {
        return buildPurchaseSummary({
            dashboardCycle,
            selectedContract
        });
    }, [dashboardCycle, selectedContract]);

    const monthlySalesChartData = useMemo(() => {
        return buildMonthlySalesChartData(dashboardCycle);
    }, [dashboardCycle]);

    const dailyAverageChartData = useMemo(() => {
        return buildDailyAverageChartData({
            dashboardCycle,
            isClosedCycle: isSelectedCycleClosed
        });
    }, [dashboardCycle, isSelectedCycleClosed]);

    const variluxMonthlyChartData = useMemo(() => {
        return buildVariluxMonthlyChartData(dashboardCycle);
    }, [dashboardCycle]);

    const handleEquipmentChange = value => {
        const equipment = equipmentOptions.find(item => {
            return String(item.value) === String(value);
        });

        setSelectedEquipment(value);

        if (!equipment) {
            setSelectedContractId("");
            setSelectedCycleId("");
            setHasManualCycleSelection(false);
            clearContractCycles();
            clearDashboardCycle();
            clearDailyGrowthSimulation();
            return;
        }

        setSelectedContractId(equipment.contractId);
        setSelectedCycleId("");
        setHasManualCycleSelection(false);
        clearContractCycles();
        clearDashboardCycle();
        clearDailyGrowthSimulation();
    };

    const handleSimulateGrossValueChange = async event => {
        setSimulateGrossValue(event.target.checked);
    };

    const cycleStatus = dashboardCycle?.cycleStatus || {
        discountPercentage: 0,
        isBelowBaseline: false,
        hasLatePaymentInPeriod: null,
        goals: {
            minimumSales: false,
            varilux: false,
            group: false
        }
    };

    const installmentSummary = useMemo(() => {
        const installment = dashboardCycle?.installments?.[0];

        return {
            originalInstallmentValue: installment?.originalValue ?? 0,
            discountProjectionValue: installment?.discountValue ?? 0,
            finalInstallmentValue: installment?.discountedValue ?? 0
        };
    }, [dashboardCycle]);

    const baselineClassName = getCycleBaselineClassName(cycleStatus.isBelowBaseline);

    const getPercentageSelectValue = value => {
        if (value === undefined || value === null || value === "") {
            return "";
        }

        return String(MonetaryUtils.parseDecimal(value));
    };

    const dailyAverageTargetOptions = useMemo(() => {
        return buildDailyAverageTargetOptions(dailyGrowthSimulation);
    }, [dailyGrowthSimulation]);

    useEffect(() => {
        const nextDiscountPercentage = dailyGrowthSimulation?.nextRange?.discountPercentage;

        if (
            nextDiscountPercentage === undefined ||
            nextDiscountPercentage === null ||
            nextDiscountPercentage === ""
        ) {
            setDailyAverageTarget("");
            return;
        }

        setDailyAverageTarget(getPercentageSelectValue(nextDiscountPercentage));
    }, [dailyGrowthSimulation]);

    const selectedDailyGrowthRange = useMemo(() => {
        return findDailyGrowthRangeByDiscount({
            dailyGrowthSimulation,
            dailyAverageTarget
        });
    }, [dailyGrowthSimulation, dailyAverageTarget]);

    const dailyGrowthToTarget = selectedDailyGrowthRange?.requiredGrowthValue ?? 0;

    useEffect(() => {
        const dailyDiscountPercentage = dashboardCycle?.sales?.daily?.discountPercentage;

        if (
            dailyDiscountPercentage === undefined ||
            dailyDiscountPercentage === null ||
            dailyDiscountPercentage === ""
        ) {
            setDailyAverageTarget("");
            return;
        }

        const dailyDiscountValue = getPercentageSelectValue(dailyDiscountPercentage);

        const hasOption = dailyAverageTargetOptions.some(option => {
            return option.value === dailyDiscountValue;
        });

        if (!hasOption && dailyAverageTargetOptions.length > 0) {
            return;
        }

        setDailyAverageTarget(dailyDiscountValue);
    }, [
        dashboardCycle?.sales?.daily?.discountPercentage,
        dailyAverageTargetOptions,
        simulateGrossValue
    ]);

    const innerPageElements = {
        backLabel: [{ label: "Dashboard do Pacto Rebates" }]
    };

    const salesValueTypeLabel = simulateGrossValue ? "Bruto" : "Líquido";

    const shouldShowDashboardCycleData = Boolean(dashboardCycle) && !isDashboardLoading;

    const renderDiscountRangeTitle = () => (
        <div className="dashboard-chart-side-header__title">
            Faixas de desconto
        </div>
    );

    const renderChartReferenceLegend = ({
        goalLabel,
        baselineLabel
    }) => (
        <div className="dashboard-chart-legend">
            <span className="dashboard-chart-legend__item dashboard-chart-legend__item--goal">
                {goalLabel}
            </span>

            <span className="dashboard-chart-legend__item dashboard-chart-legend__item--baseline">
                {baselineLabel}
            </span>
        </div>
    );

    const shouldShowClientSearch = isEssilorUser;

    const shouldShowDashboardContent =
        !isEssilorUser || Boolean(selectedClientDocument);

    const handleScrollToTop = () => {
        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    };

    return (
        <InnerPageStructure innerPageElements={innerPageElements}>
            <PageLoading
                states={[
                    {
                        loading: isDashboardLoading,
                        message: "Carregando dashboard..."
                    }
                ]}
            />

            <div className="dashboard">
                {shouldShowClientSearch && (
                    <DashboardClientSearch
                        clientSearchDocument={clientSearchDocument}
                        clientSearchFormResetKey={clientSearchFormResetKey}
                        selectedClient={selectedParticipantClient}
                        isExpanded={isClientSearchExpanded}
                        participantClients={participantClients}
                        participantClientsPagination={participantClientsPagination}
                        participantClientsLoading={participantClientsLoading}
                        onClientSearchDocumentChange={setClientSearchDocument}
                        onSearch={handleClientSearch}
                        onPageChange={handleParticipantClientsPageChange}
                        onSelectClient={handleSelectParticipantClient}
                        onToggleExpanded={handleShowClientSearchAgain}
                    />
                )}

                {shouldShowDashboardContent && (
                    <>
                        <DashboardHeader
                            equipmentOptions={equipmentOptions}
                            selectedEquipment={selectedEquipment}
                            onEquipmentChange={handleEquipmentChange}
                            purchaseSummary={purchaseSummary}
                            totalGrantedDiscounts={selectedContract?.totalDiscount ?? 0}
                        />

                        <DashboardCyclePanel
                            cycleOptions={cycleOptions}
                            selectedCycle={selectedCycleId}
                            onCycleChange={value => {
                                setHasManualCycleSelection(true);
                                setSelectedCycleId(value);
                            }}
                            referenceDateLabel={simulatedTodayLabel}
                            simulateGrossValue={simulateGrossValue}
                            onSimulateGrossValueChange={handleSimulateGrossValueChange}
                            isClosedCycle={isSelectedCycleClosed}
                            cycleStatus={cycleStatus}
                            baselineClassName={baselineClassName}
                            installmentSummary={installmentSummary}
                        />

                        {shouldShowDashboardCycleData && (
                            <div className="dashboard__grid">
                                <CardGeneric
                                    title={`Vendas Valor ${salesValueTypeLabel} - Média diária`}
                                    className="dashboard__card dashboard__card--daily-sales"
                                >
                                    <DashboardDailySalesSummary
                                        dailySales={dashboardCycle?.sales?.daily}
                                        isClosedCycle={isSelectedCycleClosed}
                                        dailyAverageTarget={dailyAverageTarget}
                                        dailyAverageTargetOptions={dailyAverageTargetOptions}
                                        onDailyAverageTargetChange={event => setDailyAverageTarget(event.target.value)}
                                        growthToTarget={dailyGrowthToTarget}
                                    />

                                    <div className="dashboard-chart-frame dashboard-chart-area">
                                        <div className="dashboard-chart-legend--monthly">
                                            {renderChartReferenceLegend({
                                                goalLabel: "Meta média diária",
                                                baselineLabel: "Baseline média diária"
                                            })}
                                        </div>

                                        {renderDiscountRangeTitle()}

                                        <DashboardLineChart
                                            type="dailySales"
                                            data={dailyAverageChartData}
                                            simulatedToday={simulatedToday}
                                            cycle={selectedCycle}
                                            isClosedCycle={isSelectedCycleClosed}
                                            goals={dashboardCycle?.sales?.daily?.goals || []}
                                            baseline={dashboardCycle?.sales?.daily?.dailyBaseline}
                                        />
                                    </div>
                                </CardGeneric>

                                <CardGeneric
                                    title={`Vendas Valor ${salesValueTypeLabel} - Média mensal`}
                                    className="dashboard__card dashboard__card--monthly-sales"
                                >
                                    <DashboardMonthlySalesSummary
                                        monthlySales={dashboardCycle?.sales?.monthly}
                                        cycleStatus={cycleStatus}
                                        isClosedCycle={isSelectedCycleClosed}
                                    />

                                    <div className="dashboard-chart-frame dashboard-chart-area">
                                        <div className="dashboard-chart-legend--monthly">
                                            {renderChartReferenceLegend({
                                                goalLabel: "Meta média mensal",
                                                baselineLabel: "Baseline média mensal"
                                            })}
                                        </div>

                                        {renderDiscountRangeTitle()}

                                        <DashboardLineChart
                                            type="monthlySales"
                                            data={monthlySalesChartData}
                                            simulatedToday={simulatedToday}
                                            cycle={selectedCycle}
                                            isClosedCycle={isSelectedCycleClosed}
                                            goals={dashboardCycle?.sales?.monthly?.goals || []}
                                            baseline={dashboardCycle?.sales?.monthly?.monthlyBaseline}
                                        />
                                    </div>
                                </CardGeneric>

                                <CardGeneric
                                    title="Varilux - Mensal"
                                    className="dashboard__card dashboard__card--varilux"
                                >
                                    <DashboardVariluxSummary
                                        varilux={dashboardCycle?.varilux}
                                        isClosedCycle={isSelectedCycleClosed}
                                    />

                                    <div className="dashboard-chart-frame dashboard-chart-area">
                                        <div className="dashboard-chart-legend--varilux">
                                            {renderChartReferenceLegend({
                                                goalLabel: "Meta de pares",
                                                baselineLabel: "Baseline de pares"
                                            })}
                                        </div>

                                        <DashboardLineChart
                                            type="variluxMonthly"
                                            data={variluxMonthlyChartData}
                                            simulatedToday={simulatedToday}
                                            cycle={selectedCycle}
                                            isClosedCycle={isSelectedCycleClosed}
                                            variluxGoal={dashboardCycle?.varilux?.variluxGoal}
                                            variluxBaseline={dashboardCycle?.varilux?.variluxBaseline}
                                        />
                                    </div>
                                </CardGeneric>
                            </div>
                        )}

                        {shouldShowDashboardCycleData && (
                            <div className="dashboard__scroll-top">
                                <AppButton
                                    type="button"
                                    onClick={handleScrollToTop}
                                    endIcon="circleArrowUp"
                                >
                                    Voltar ao topo
                                </AppButton>
                            </div>
                        )}

                        <AdditionalInformation
                            className="dashboard-disclaimer"
                            title="Informações importantes"
                            items={[
                                "* Os descontos alcançados no fechamento deste ciclo serão aplicados nas parcelas em aberto do próximo ciclo.",
                                "* O atingimento das metas de Varilux e de Net Sales do Grupo são pré requisito para garantir o desconto.",
                                "* Em caso de inadimplência do cliente no momento da apuração, os benefícios podem ser reduzidos conforme contrato."
                            ]}
                        />
                    </>
                )}
            </div>
        </InnerPageStructure>
    );
};

export default Dashboard;